import React from 'react';
import ScorecardNavigation from './ScorecardNavigation';

type Access = 'full' | 'tenant' | 'location' | 'none';

interface FeatureRow {
  feature: string;
  description: string;
  admin: Access;
  corporate: Access;
  manager: Access;
  // feat-191-evaluator-role (C5). The evaluator column is '—' on every tenant-facing
  // row and that is the whole point of the role: StaffRoute and createPermissionChecker
  // are allowlists that already deny it, so this column documents behavior that is
  // true in code today with no change to either file. The one row it is not '—' on is
  // Interaction Evaluations.
  evaluator: Access;
}

const ROWS: FeatureRow[] = [
  {
    feature: 'Live Servers',
    description: 'Real-time list of servers currently on shift.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Server Scorecard',
    description: 'Per-server performance across greetings, upsells, etc.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Manager / Store Scorecard',
    description: 'Aggregated manager and store performance.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Leaderboard',
    description: 'Ranked server performance within a period.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Upsell Dashboard',
    description: 'Upsell breakdown and trends.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'User Management',
    description: 'Add, edit, and deactivate servers and managers.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Prompt Management',
    description: 'Edit the Claude prompt templates that power interaction detection.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'none',
    evaluator: 'none',
  },
  {
    feature: 'Administration',
    description: 'This section — entry point for tenant configuration. Tile list adapts to role.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'tenant',
    evaluator: 'none',
  },
  {
    feature: 'Learn Module (quiz editor)',
    description: 'Edit units, lessons, and questions for the Flutter Learn screen. Quiz content is tenant-wide.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'tenant',
    evaluator: 'none',
  },
  {
    feature: 'Phrase Guidelines',
    description: 'Positive/negative phrases appended to the LLM prompt during transcript annotation.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'tenant',
    evaluator: 'none',
  },
  {
    // feat-251: Trivia is deliberately NOT part of the manager curriculum grant —
    // every route in the backend's admin_trivia.py stays corporate-only, so the
    // Administration page hides the Trivia tile from managers.
    feature: 'Trivia',
    description: 'Short lore questions shown as a pop-up when a server opens the app. No XP or scorecard effect.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'none',
    evaluator: 'none',
  },
  {
    feature: 'Send a Notification',
    description: 'Compose one-off notifications to servers or managers. Audience scope follows role.',
    admin: 'full',
    corporate: 'tenant',
    manager: 'location',
    evaluator: 'none',
  },
  {
    feature: 'Permissions (this page)',
    description: 'Reference doc of feature access by role. Admin-only.',
    admin: 'full',
    corporate: 'none',
    manager: 'none',
    evaluator: 'none',
  },
  {
    // feat-191: the only row where Evaluator is not '—'. Cross-tenant for both
    // Admin and Evaluator because the six eval endpoints carry no tenant filter
    // and never did — grading is internal QA that spans every customer (D2).
    feature: 'Interaction Evaluations',
    description:
      'Grade detected interactions Correct/Incorrect across all tenants. The three /restaurant/internal/interaction-evaluations pages — the entire Evaluator surface. The per-evaluator stats panel on the dashboard is Admin-only.',
    admin: 'full',
    corporate: 'none',
    manager: 'none',
    evaluator: 'full',
  },
];

const ACCESS_STYLES: Record<Access, { label: string; classes: string }> = {
  full: {
    label: 'Full',
    classes: 'bg-green-100 text-green-800 border border-green-200',
  },
  tenant: {
    label: 'Tenant-scoped',
    classes: 'bg-blue-50 text-blue-800 border border-blue-200',
  },
  location: {
    label: 'Location-scoped',
    classes: 'bg-amber-50 text-amber-800 border border-amber-200',
  },
  none: {
    label: '—',
    classes: 'bg-gray-100 text-gray-500 border border-gray-200',
  },
};

const AccessCell: React.FC<{ access: Access }> = ({ access }) => {
  const style = ACCESS_STYLES[access];
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${style.classes}`}
    >
      {style.label}
    </span>
  );
};

const PermissionsMatrix: React.FC = () => {
  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-display font-semibold text-ink mb-1">Permissions</h1>
          <p className="text-sm text-inkMuted">
            Read-only reference of which features each role can access. This reflects current
            behavior in code — it is not editable.
          </p>
        </div>

        <div className="bg-white border border-softBorder rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-dark">
              <tr>
                <th className="text-left font-semibold text-ink px-4 py-3 w-1/3">Feature</th>
                <th className="text-left font-semibold text-ink px-4 py-3">Admin</th>
                <th className="text-left font-semibold text-ink px-4 py-3">Corporate</th>
                <th className="text-left font-semibold text-ink px-4 py-3">Manager</th>
                <th className="text-left font-semibold text-ink px-4 py-3">Evaluator</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, idx) => (
                <tr
                  key={row.feature}
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-cream/50'}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-ink">{row.feature}</div>
                    <div className="text-xs text-inkMuted mt-0.5">{row.description}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <AccessCell access={row.admin} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <AccessCell access={row.corporate} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <AccessCell access={row.manager} />
                  </td>
                  <td className="px-4 py-3 align-top">
                    <AccessCell access={row.evaluator} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 text-xs text-inkMuted space-y-1">
          <div>
            <span className="font-semibold text-ink">Full</span> — unrestricted access across all
            tenants and locations.
          </div>
          <div>
            <span className="font-semibold text-ink">Tenant-scoped</span> — access limited to the
            user's own tenant.
          </div>
          <div>
            <span className="font-semibold text-ink">Location-scoped</span> — access limited to
            the user's own location within their tenant.
          </div>
          <div>
            <span className="font-semibold text-ink">—</span> — no access; feature is hidden or
            returns an access-denied message.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
